import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { CheckCircle2, CircleAlert, Info, X } from "lucide-react";

type NotificationTone = "success" | "error" | "info";
type Notice = { id: number; message: string; tone: NotificationTone };

type NotificationContextValue = {
  notify: (message: string, tone?: NotificationTone) => void;
};

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notices, setNotices] = useState<Notice[]>([]);

  const dismiss = useCallback((id: number) => {
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, tone: NotificationTone = "info") => {
      const id = Date.now() + Math.random();
      setNotices((current) => [...current, { id, message, tone }]);
      window.setTimeout(() => dismiss(id), 6_000);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="toast-region" aria-live="polite" aria-label="Oznámení">
        {notices.map((notice) => (
          <div key={notice.id} className={`toast toast--${notice.tone}`}>
            {notice.tone === "success" ? <CheckCircle2 /> : notice.tone === "error" ? <CircleAlert /> : <Info />}
            <span>{notice.message}</span>
            <button type="button" onClick={() => dismiss(notice.id)} aria-label="Zavřít oznámení">
              <X />
            </button>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotification(): NotificationContextValue {
  const value = useContext(NotificationContext);
  if (!value) throw new Error("useNotification must be used inside NotificationProvider");
  return value;
}
